/**
 * @voltxampere/web — Command palette type contracts (Sprint 22 C1)
 *
 * The Cmd+K command palette is the unified action surface defined
 * in BLUEPRINT §14. Every user-visible action — file save, place a
 * resistor, run AC sweep, switch panes — registers as a `Command`
 * here, so:
 *
 *  1. The palette can search across the full set with one fuzzy
 *     query (see `@voltxampere/shared/utils/fuzzy.ts`).
 *  2. Shortcuts (Sprint 17 carry-over) become accelerators; absent
 *     shortcuts still get a discoverable home.
 *  3. Async commands (export PDF, run long simulation) declare
 *     their async-ness so the palette UI can show progress.
 *
 * Title / description are stored as **i18n keys** (e.g.
 * `"command.file.save"`), not localised strings — the palette
 * resolves them through the same `t()` helper everywhere else, so
 * locale switches re-render correctly without re-registering
 * commands.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

/**
 * Eight canonical category buckets per BLUEPRINT §14.1, plus three
 * v3-specific extensions (`pane`, `preset`, `developer`):
 *
 *  - `file`         File-system, share-link, vxa import/export.
 *  - `edit`         Undo/redo, copy/paste, rotate, delete.
 *  - `view`         Zoom, grid toggle, Bode toggle.
 *  - `selection`    Select-all / deselect / invert (S23+ work).
 *  - `tool`         Canvas mode (select, wire, stamp, pan, probe).
 *  - `place`        Drop one of the registered components on the
 *                   canvas in stamp mode.
 *  - `simulation`   Run/stop DC, transient, AC; live mode toggle.
 *  - `pane`         Switch between AÇILIŞ/SİMÜLATÖR/DEVRELER/
 *                   INSPECTOR/AYARLAR pages (Sprint 18 router).
 *  - `preset`       Open a built-in circuit (S15 transient, S20 AC).
 *  - `help`         Shortcuts overlay, About, GitHub link.
 *  - `developer`    Debug-only commands gated by `?debug=1`.
 */
export type CommandCategory =
  | "file"
  | "edit"
  | "view"
  | "selection"
  | "tool"
  | "place"
  | "simulation"
  | "pane"
  | "preset"
  | "help"
  | "developer";

/**
 * A registered palette command. All fields except `id`, `titleKey`,
 * `category`, and `execute` are optional — most commands only need
 * the basics, and the optional `when` / `shortcut` / `descriptionKey`
 * surface as additional palette affordances when present.
 */
export interface Command {
  /**
   * Stable identifier in `<category>.<verb>` form, e.g.
   * `"file.save"`. Used as the registry key and as the persistence
   * id for recent-commands history (Sprint 22 C4).
   */
  readonly id: string;
  /** i18n key for the title, resolved at query time via `t()`. */
  readonly titleKey: string;
  /** Optional i18n key for the secondary line in the palette row. */
  readonly descriptionKey?: string;
  /** Category bucket (see {@link CommandCategory}). */
  readonly category: CommandCategory;
  /**
   * Optional inline SVG payload (without the `<svg>` wrapper) or
   * named icon. The palette renderer decides how to display it.
   */
  readonly icon?: string;
  /**
   * Display string for the keyboard shortcut, if one exists.
   * Purely cosmetic — the actual key binding is handled by
   * `apps/web/src/shortcuts/`. Format: "Cmd+K", "Shift+P", etc.
   */
  readonly shortcut?: string;
  /**
   * Availability gate. When `false`, the command is filtered from
   * search results and `execute()` returns silently. Reserved for
   * commands that only make sense in certain pane / selection
   * states (e.g. `edit.copy` requires non-empty selection).
   */
  readonly when?: () => boolean;
  /**
   * Action to invoke. Returning a Promise tells the palette UI
   * to show a progress indicator (Sprint 22 C2).
   */
  readonly execute: () => void | Promise<void>;
}

/** Options for {@link CommandRegistry.search}. */
export interface CommandSearchOptions {
  /** Maximum number of results to return; default 50. */
  readonly limit?: number;
  /**
   * i18n key resolver. Defaults to identity (returns the key
   * unchanged) so unit tests can search the registry without a
   * full i18n bootstrap; the palette UI passes its real `t()`
   * resolver so titles match the visible strings.
   */
  readonly resolveKey?: (key: string) => string;
}

/** One ranked result row from {@link CommandRegistry.search}. */
export interface CommandSearchResult {
  readonly command: Command;
  readonly score: number;
  /** Positions inside the resolved title string that matched. */
  readonly matchedIndices: readonly number[];
}

/** Thrown when a command id is registered twice. */
export class DuplicateCommandIdError extends Error {
  override readonly name = "DuplicateCommandIdError";
  constructor(readonly id: string) {
    super(`CommandRegistry: duplicate command id "${id}"`);
  }
}

/** Thrown when {@link CommandRegistry.execute} hits an unknown id. */
export class UnknownCommandIdError extends Error {
  override readonly name = "UnknownCommandIdError";
  constructor(readonly id: string) {
    super(`CommandRegistry: command "${id}" is not registered`);
  }
}
